import { ArrowChevronRightIcon } from '@tableau/icons/ArrowChevronRightIcon';
import { NotificationCheckIcon } from '@tableau/icons/NotificationCheckIcon';
import { OperationsSubtractMinusIcon } from '@tableau/icons/OperationsSubtractMinusIcon';
import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { Formatters, Messages } from '../../../gen-src/Localize';
import { makeTestId } from '../../utils/Declarative';
import { generateId, scrollIntoView } from '../../utils/DomUtil';
import { ElemAttrs } from '../../utils/HtmlAttrs';
import { LiveAnnouncer, VisuallyHiddenStyle } from '../../utils/LiveAnnouncer';
import { EventUtilsOptOut, mergeEventHandlers, MutuallyExclude, PropsAndElementAttrs, RequireLabel, useEffectExceptOnMount } from '../../utils/ReactHelpers';
import { ActivitySpinnerWidget } from '../ActivitySpinnerWidget';
import { FilteringListMessages } from '../List/FilteringList';
import { ItemValueAttr, ListIsActiveContext } from '../List/List';
import { isFocusableItem } from '../List/ListItemUtils';
import { ListIsActiveClass } from '../List/ListStyles';
import { ChangeEventWithCompositionStatus, TextFieldWidget } from '../TextField/TextFieldWidget';
/* eslint-disable max-len */
import { constructTreeNodeHierarchy, filterTree, getAncestorValues, getDescendantLeafValues, getFocusableValuesAndTreeHeight, isContentLoading, LoadingItemContent, TreeHierarchyInfo, TreeItem, TreeItemFilterFn, TreeNode } from './TreeHelpers';
import { CheckContainerStyleName, CheckIconSize, ChevronContainerStyleName, ContentContainerStyleName, DefaultIconSize, LabelContainerStyleName, RootContainerStyleName, SearchFieldStyleName, SectionTitleStyleName, SeparatorStyleName, SpinnerWrapperStyleName, TreeHeightCSSVarName, TreeItemStyleName, TreeNodeDisabledClassName, TreeNodeLevelCSSVarName, TreeNodeLoadingClassName, TreeStyleName, TreeUnselectableParentsClassName } from './TreeStyles';
/* eslint-enable max-len */

export { LoadingItemContent, TreeItem, TreeItemFilterFn };

type TreeSelectionProps = {
  /**
   * When selection state is uncontrolled (i.e. managed internally by `TreeWidget`),
   * a value may be provided for the selected item in a single select tree on initial render.
   */
  initiallySelectedValue?: string;
  /**
   * When selection state is uncontrolled (i.e. managed internally by `TreeWidget`),
   * a set of values may be provided for the selected leaf items in a multiselect tree on initial render.
   * Only leaf items need to be specified because the selection state and indeterminate state of parent items
   * are always automatically calculated from the overall selection state of their descendant leaf items.
   */
  initiallySelectedValues?: Set<string>;
  /**
   * When selection state is controlled (i.e. managed externally by parent component),
   * a value must be provided for the selected item in a single select tree.
   *
   * When no item should be selected, pass in `null`.
   * If not provided, the selection state will be managed internally by `TreeWidget`.
   */
  selectedValue?: string | null;
  /**
   * When selection state is controlled (i.e. managed externally by parent component),
   * a set of values must be provided for the selected leaf items in a multiselect tree.
   * Only leaf items need to be specified because the selection state and indeterminate state of parent items
   * are always automatically calculated from the overall selection state of their descendant leaf items.
   *
   * When no items should be selected, provide an empty set.
   * If not provided, the selection state will be managed internally by `TreeWidget`.
   */
  selectedValues?: Set<string>;
};

export type TreeProps<TreeItemType extends TreeItem = TreeItem> = MutuallyExclude<TreeSelectionProps> & {
  /** Applied to the search field `<input>` element, useful primarily for avoiding heuristics in Chrome. */
  autoComplete?: string;
  /** Focus the search field `<input>` element on mount. */
  autoFocus?: boolean;
  children?: never;
  /**
   * When expansion state is controlled (i.e. managed externally by parent component),
   * a set of values must be provided for the expanded items.
   *
   * When no items should be expanded, provide an empty set.
   * If not provided, the expansion state will be managed internally by `TreeWidget`.
   */
  expandedValues?: Set<string>;
  /** Set to true to hide the search field. */
  hideSearchField?: boolean;
  /**
   * When expansion state is uncontrolled (i.e. managed internally by `TreeWidget`),
   * a set of values may be provided for expanded items on initial render.
   */
  initiallyExpandedValues?: Set<string>;
  /**
   * By default the text in the search field will be compared against `TreeItem.value` using case-insensitive substring match.
   * This prop can be used to override the comparison function and is required when using `TreeItem.content` so that logical matches can be displayed.
   *
   * NOTE: items rendered as section titles or separators are handled separately and always preserved when any subsequent sibling item is one of the matches.
   */
  itemFilterFn?: TreeItemFilterFn<TreeItemType>;
  /**
   * Array of hierarchical items. If `null`, the entire tree will be rendered as a large spinner to indicate items are still loading.
   *
   * The hierarchy is computed using the `value` and `parentValue` of each item. The ordering is preserved for items that appear on the same level.
   *
   * For example, if this array contains `{ value: 'x' }, { value: 'y' }, { value: 'a', parentValue: 'y' }, { value: 'b', parentValue: 'x' }`,
   * the hierarchy will end up as:
   * @example
   *  x
   *    b
   *  y
   *    a
   */
  items: Array<TreeItemType> | null;
  /**
   * Set to true to allow more than one item to be selected.
   *
   * NOTE: when selection state is uncontrolled (i.e. managed internally by `TreeWidget`), selecting or deselecting
   * a parent item in a multiselect tree will automatically select or deselect its descendants.
   * No matter selection state is controlled or uncontrolled, the selection state and indeterminate state
   * of parent items are always automatically calculated from the overall selection state of their descendants.
   */
  multiselect?: boolean;
  /**
   * Callback invoked when the user performs an action, such as clicking on the chevron icon, to expand or collapse the item with the given value.
   * When managing expansion state in controlled mode, this is where external state should be updated.
   */
  onExpansionChange?: (value: string) => void;
  /**
   * Callback invoked when the user changes the value in the search field.
   * The new value can be accessed via `target.value`, and `isComposing` indicates whether the user is still using an IME, e.g. to input CJK text.
   * This callback may be useful for e.g. fetching new items based on the new value, or custom text formatting.
   */
  onSearchTextChange?: (e: ChangeEventWithCompositionStatus) => void;
  /**
   * Callback invoked when the user performs an action, such as pressing the 'Enter' key, to select or deselect the item with the given value.
   * When managing selection state in controlled mode, this is where external state should be updated.
   */
  onSelectionChange?: (value: string) => void;
  /**
   * Determines what action is triggered when interacting with parent items: specifically, clicking on
   * the non-chevron part of a parent item or pressing the 'Space' or 'Enter' key. If 'toggleExpansion'
   * is chosen, parent nodes cannot be selected.
   *
   * No matter this value, clicking the chevron icon or pressing the 'ArrowLeft' or 'ArrowRight' keys
   * will toggle expansion.
   *
   * @default ```'toggleSelection'```
   */
  parentNodeActivationAction?: 'toggleSelection' | 'toggleExpansion';
  /** Placeholder text applied to the search field `<input>` element. */
  searchPlaceholder?: string;
  /** Value of the search field `<input>` element. */
  searchText?: string;
  /**
   * Test id to be attached to the root wrapper element `data-tb-test-id` attribute with suffix `-TreeWidget`.
   * Also included as part of the `data-tb-test-id` attribute applied to the search field `<input>` element.
   */
  testId?: string;
  /**
   * HTML attributes to be forwarded to the `role=tree` element.
   * This may be useful for e.g. overriding the autogenerated DOM ID, or overriding the `tabindex` for a custom combobox implementation.
   */
  treeElemAttrs?: ElemAttrs;
  treeRef?: React.Ref<TreeInstance>;
};

export type TreeInstance = {
  /** The value of the currently pseudo-focused item, or `undefined` when no item is pseudo-focused. */
  activeValue?: string;
  /**
   * Expands all parent items.
   * This is expected to be used only when expansion state is uncontrolled (i.e. managed internally by `TreeWidget`).
   */
  expandAll: () => void;
  /**
   * A set of values for the currently expanded items.
   * This is expected to be used only when expansion state is uncontrolled (i.e. managed internally by `TreeWidget`).
   */
  expandedValues: Set<string>;
  /**
   * Scrolls an item with the given value into view.
   * When expansion state is uncontrolled (i.e. managed internally by `TreeWidget`), this method will automatically expand any ancestor items.
   * When expansion state is controlled (i.e. managed externally by parent component), make sure to include the values of ancestor items in `expandedValues`
   * and then call this method after the next render.
   *
   * @param block the scroll behavior along the vertical axis, defaults to ```'nearest'```
   */
  scrollIntoView: (value: string, block?: ScrollLogicalPosition) => void;
  /**
   * Selects all selectable items.
   * This is expected to be used only when selection state is uncontrolled (i.e. managed internally by `TreeWidget`) and `multiselect` is set to true.
   */
  selectAll: () => void;
  /**
   * The value of the currently selected item, or `undefined` when no item is selected.
   * This is expected to be used only when selection state is uncontrolled (i.e. managed internally by `TreeWidget`) and `multiselect` is falsy.
   */
  selectedValue?: string;
  /**
   * A set of values for the currently selected leaf items.
   * This is expected to be used only when selection state is uncontrolled (i.e. managed internally by `TreeWidget`) and `multiselect` is set to true.
   */
  selectedValues: Set<string>;
  /**
   * Toggles the expansion state of the item with the given value.
   * This is expected to be used only when expansion state is uncontrolled (i.e. managed internally by `TreeWidget`).
   *
   * @param expand optionally pass in ```true``` to expand or ```false``` to collapse
   */
  toggleExpansion: (value: string, expand?: boolean) => void;
  /**
   * Toggles the selection state of the item with the given value.
   * This is expected to be used only when selection state is uncontrolled (i.e. managed internally by `TreeWidget`).
   *
   * NOTE: when `multiselect` is set to true, toggling a parent item will automatically toggle its descendants.
   * @param select optionally pass in ```true``` to select or ```false``` to deselect
   */
  toggleSelection: (value: string, select?: boolean) => void;
};

type RequireLabelIncludingVisibleLabel = (RequireLabel & { label?: React.ReactNode }) | { label: React.ReactNode }; // W-16960626 typedef refactoring

/**
 * `TreeWidget` allows users to browse and select from items nested in a hierarchy.
 * Can be used as either a controlled or uncontrolled component when managing expansion state and/or selection state.
 *
 * In controlled mode, the parent component is responsible for handling changes signaled by the callback props
 * and passing in the props: `expandedValues`, `selectedValue` or `selectedValues`.
 * In uncontrolled mode, the state is handled internally by `TreeWidget` but can be accessed by saving a ref
 * and can be defaulted by passing in the props: `initiallyExpandedValues`, `initiallySelectedValue` or `initiallySelectedValues`.
 *
 * All HTML props are forwarded to the root wrapper element, with the following exceptions:
 * - `autoComplete` is applied to the search field `<input>` element
 * - `aria-describedby` is applied to either the search field `<input>` element, or the `role=tree` element when `hideSearchField` is set to true.
 * - `aria-label` and `aria-labelledby` are applied to both the search field `<input>` element and the `role=tree` element
 */
export function TreeWidget<T extends TreeItem = TreeItem>(props: PropsAndElementAttrs<TreeProps<T>> & RequireLabelIncludingVisibleLabel): React.ReactElement {
  // eslint-disable-next-line max-len
  const { 'aria-describedby': ariaDescribedby, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledby, autoComplete, autoFocus, expandedValues, hideSearchField, initiallyExpandedValues, initiallySelectedValue, initiallySelectedValues, itemFilterFn, items, label, multiselect, onExpansionChange, onSearchTextChange, onSelectionChange, parentNodeActivationAction, searchPlaceholder, searchText: searchTextControlled, selectedValue, selectedValues, testId, treeElemAttrs, treeRef, ...htmlAttrs } = props;

  const [treeLabelId] = React.useState(generateId());
  const [scrollRequest, setScrollRequest] = React.useState<{ value: string, block: ScrollLogicalPosition } | null>(null); // used for deferred scrolling
  const isComposing = React.useRef(false);
  const [searchTextInternal, setSearchText] = React.useState('');
  const searchText = hideSearchField ? '' : searchTextControlled ?? searchTextInternal; // always use empty string when search field is not shown
  const searchFieldRef = React.useRef<HTMLInputElement>(null);
  const treeElemRef = React.useRef<HTMLUListElement>(null);
  const { announceCount, clearAnnounceCount } = useDebouncedAnnouceCount(searchFieldRef, treeElemRef);
  const { treeAttributes, treeHierarchyInfo, treeState } = useTree(props, searchText);

  React.useEffect(() => {
    window.setTimeout(() => {
      autoFocus && searchFieldRef.current?.focus();
    });
    // intentionally using empty dependencies to run this effect only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffectExceptOnMount(() => {
    items && document.activeElement === searchFieldRef.current ? announceCount() : clearAnnounceCount();
    return () => clearAnnounceCount();
  }, [announceCount, clearAnnounceCount, items, searchFieldRef, searchText]);

  useEffectExceptOnMount(() => {
    onSearchTextChange?.({ isComposing: isComposing.current, target: { value: searchTextInternal } } as ChangeEventWithCompositionStatus);
  }, [searchTextInternal]);

  React.useImperativeHandle(treeRef, (): TreeInstance => ({
    /* Properties */
    get activeValue() {
      return treeState.activeValue;
    },
    set activeValue(newActiveValue) {
      treeState.updateActiveValue(newActiveValue);
    },
    get expandedValues() {
      return treeState.expandedValues;
    },
    set expandedValues(newExpandedValues) {
      treeState.setExpandedValues(newExpandedValues);
    },
    get selectedValue() {
      return treeState.selectedValues.size === 1 ? treeState.selectedValues.keys().next().value as string : undefined;
    },
    set selectedValue(newSelectedValue) {
      !multiselect && treeState.setSelectedValues(newSelectedValue ? new Set(newSelectedValue) : new Set());
    },
    get selectedValues() {
      return treeState.selectedValues;
    },
    set selectedValues(newSelectedValues) {
      multiselect && treeState.setSelectedValues(newSelectedValues);
    },

    /* Methods */
    expandAll: () => treeState.setExpandedValues(treeHierarchyInfo.allParentValues),
    scrollIntoView: (value, block) => {
      treeState.addToExpandedValues(getAncestorValues(treeHierarchyInfo.nodeMapByValue, value));
      // The previous line may result in a change to expansion state in order to show the item with the given value,
      // and since state update is async, we need to rely on the way React batches state updates and trigger the scroll effect after the next render.
      setScrollRequest({ value, block: block ?? 'nearest' });
    },
    selectAll: () => multiselect && treeState.setSelectedValues(treeHierarchyInfo.allSelectableLeafValues),
    toggleExpansion: (value, expand) => treeState.toggleExpansion(value, expand),
    toggleSelection: (value, select) => treeState.toggleSelection(value, select),
  }));

  React.useEffect(function deferredScrolling() {
    if (scrollRequest) {
      scrollIntoView(document.getElementById(treeItemId(treeAttributes.id, scrollRequest.value)), scrollRequest.block);
    }
    // intentionally only depend on `scrollRequest` which gets updated in the `scrollIntoView` instance method in order to defer scrolling
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollRequest]);

  const handleContainerFocusCapture = React.useCallback<React.FocusEventHandler<HTMLElement>>(e => {
    // NOTE: this focus handler actually causes a bug for mouse interactions because the click handler never gets called when a scroll-into-view event
    // happens before the mouse click finishes, but we are not fixing this right now since real users don't need to scroll to see the search field.
    // For details see https://salesforce-internal.slack.com/archives/G9MFJED2Q/p1720720853982399.
    if (searchFieldRef.current && e.target !== searchFieldRef.current) {
      searchFieldRef.current.focus(); // make sure focus stays on the search field when clicking on items
    }
  }, [searchFieldRef]);

  const handleContainerKeyDown = React.useCallback<React.KeyboardEventHandler<HTMLElement>>(e => {
    const { activeValue, expandedValues, focusableValues, toggleExpansion, toggleSelection, updateActiveValue } = treeState;
    if (!focusableValues.length) { return; } // early return because there's no focusable item to interact with
    if (e.key === ' ' && !hideSearchField) { return; } // early return because space should be treated as a regular character in the search field

    if (!activeValue) {
      if (e.key === 'ArrowDown' && !hideSearchField) {
        updateActiveValue(focusableValues[0]); // special case for initializing pseudo focus
        e.preventDefault();
        e.stopPropagation();
      }
    } else {
      const currentTreeNode = treeHierarchyInfo.nodeMapByValue.get(activeValue);
      const indexInAllFocusables = focusableValues.indexOf(activeValue);
      const isExpanded = expandedValues.has(activeValue);
      const focusableChildNodes = currentTreeNode?.children.filter(node => isFocusableItem(node.item));
      const firstFocusableChildValue = focusableChildNodes?.find(node => focusableValues.includes(node.item.value!))?.item.value;
      const toggleSelectionOrExpansion = (parentNodeActivationAction === 'toggleExpansion' && currentTreeNode?.children.length)
        ? ((e: React.KeyboardEvent<HTMLElement>) => !e.repeat && toggleExpansion(activeValue))
        : ((e: React.KeyboardEvent<HTMLElement>) => !e.repeat && toggleSelection(activeValue));
      const keyDownHandlers: { [key: string]: (e: React.KeyboardEvent<HTMLElement>) => void } = Object.freeze({
        ' ': toggleSelectionOrExpansion,
        'Enter': toggleSelectionOrExpansion,
        'ArrowRight': () => {
          if (focusableChildNodes?.length) {
            if (isExpanded) {
              firstFocusableChildValue && updateActiveValue(firstFocusableChildValue);
            } else {
              toggleExpansion(activeValue, true);
            }
          }
        },
        'ArrowLeft': () => {
          if (isExpanded) {
            toggleExpansion(activeValue, false);
          } else {
            const parentValue = currentTreeNode?.item.parentValue;
            parentValue && updateActiveValue(parentValue);
          }
        },
        'ArrowUp': () => updateActiveValue(focusableValues[Math.max(0, indexInAllFocusables - 1)]),
        'ArrowDown': () => updateActiveValue(focusableValues[Math.min(focusableValues.length - 1, indexInAllFocusables + 1)]),
        'Home': () => updateActiveValue(focusableValues[0]),
        'End': () => updateActiveValue(focusableValues[focusableValues.length - 1]),
      });

      if (keyDownHandlers[e.key]) {
        keyDownHandlers[e.key](e);
        e.preventDefault();
        e.stopPropagation();
      }
    }
  }, [hideSearchField, parentNodeActivationAction, treeHierarchyInfo.nodeMapByValue, treeState]);

  let children = useTreeChildElements(treeAttributes.id, treeHierarchyInfo, treeState, multiselect);
  let descriptionIds = ariaDescribedby;
  if (!items) {
    // need a hidden description because the alt text in `ActivitySpinnerWidget` does not appear as part of the accessible name
    const hiddenDescriptionId = `${treeAttributes.id}%%loading-description`;
    descriptionIds = ariaDescribedby ? `${ariaDescribedby} ${hiddenDescriptionId}` : hiddenDescriptionId;
    children = <>
      <span className={SpinnerWrapperStyleName}>{<ActivitySpinnerWidget testId={testId ?? ''} />}</span>
      <span id={hiddenDescriptionId} style={VisuallyHiddenStyle}>{Messages.loading()}</span>
    </>;
  } else if (searchText && !treeState.matchedValues.size) {
    children = <span>{FilteringListMessages.noMatches()}</span>;
  }

  let treeLabelIds = ariaLabelledby;
  const showTreeLabel = hideSearchField && label;
  if (showTreeLabel) {
    treeLabelIds = ariaLabelledby ? `${ariaLabelledby} ${treeLabelId}` : treeLabelId;
  }

  return <div
    data-tb-test-id={makeTestId(testId, TreeWidget.displayName)}
    {...htmlAttrs}
    className={TypeStyle.classes(RootContainerStyleName, htmlAttrs.className)}
    onFocusCapture={mergeEventHandlers(handleContainerFocusCapture, htmlAttrs.onFocusCapture)}
    onKeyDown={mergeEventHandlers(handleContainerKeyDown, htmlAttrs.onKeyDown)}
  >
    {!hideSearchField &&
      <TextFieldWidget
        aria-activedescendant={treeState.activeValue ? treeItemId(treeAttributes.id, treeState.activeValue) : undefined}
        aria-controls={treeAttributes.id}
        aria-describedby={descriptionIds}
        aria-expanded
        aria-haspopup='tree'
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        autoComplete={autoComplete}
        className={SearchFieldStyleName}
        kind='search'
        label={label}
        onBlur={() => {
          clearAnnounceCount();
          treeState.updateActiveValue(undefined);
        }}
        onChange={e => {
          isComposing.current = e.isComposing;
          setSearchText(e.target.value);
        }}
        onClear={() => {
          isComposing.current = false;
          setSearchText('');
        }}
        onFocus={() => items && announceCount(5000)}
        placeholder={searchPlaceholder}
        ref={searchFieldRef}
        role='combobox'
        testId={testId || ''}
        value={searchText}
      />
    }
    {showTreeLabel && <label id={treeLabelId}>{label}</label>}
    {<ListIsActiveContext.Consumer>{listIsActive =>
      <ul
        {...EventUtilsOptOut}
        tabIndex={hideSearchField ? 0 : -1} // this should be overridable in case an outside component wants to control the tree in a combobox way
        {...treeElemAttrs}
        aria-describedby={hideSearchField ? descriptionIds : undefined}
        aria-label={ariaLabel}
        aria-labelledby={treeLabelIds}
        children={children}
        className={TypeStyle.classes(
          TreeStyleName,
          {
            [ListIsActiveClass]: listIsActive,
            [TreeUnselectableParentsClassName]: parentNodeActivationAction === 'toggleExpansion',
          },
          treeElemAttrs?.className,
        )}
        ref={treeElemRef}
        role='tree'
        {...treeAttributes}
      />
    }</ListIsActiveContext.Consumer>}
  </div>;
}
TreeWidget.defaultProps = { parentNodeActivationAction: 'toggleSelection' };
TreeWidget.displayName = 'TreeWidget';

export const treeItemId = (treeId: string, itemValue: string): string => {
  return `${treeId}%%${itemValue}`;
}

type TreeState = {
  activeValue?: string;
  addToExpandedValues: (values: Array<string>) => void;
  expandedValues: Set<string>;
  focusableValues: Array<string>;
  matchedValues: Set<string>;
  selectedValues: Set<string>;
  setExpandedValues: (values: Set<string>) => void;
  setSelectedValues: (values: Set<string>) => void;
  toggleExpansion: (value?: string, expand?: boolean) => void;
  toggleSelection: (value?: string, select?: boolean) => void;
  updateActiveValue: (value?: string) => void;
};

const useTree = (props: PropsAndElementAttrs<TreeProps>, searchText: string): {
  treeAttributes: React.HTMLAttributes<HTMLUListElement> & Required<Pick<React.HTMLAttributes<HTMLUListElement>, 'id'>>,
  treeHierarchyInfo: TreeHierarchyInfo,
  treeState: TreeState,
} => {
  const { hideSearchField, itemFilterFn, items, multiselect, parentNodeActivationAction, treeElemAttrs } = props;
  const [defaultDomId] = React.useState(generateId());
  const treeId = treeElemAttrs?.id ?? defaultDomId;

  const treeHierarchyInfo = React.useMemo(
    () => constructTreeNodeHierarchy(items, parentNodeActivationAction === 'toggleExpansion', multiselect),
    [items, multiselect, parentNodeActivationAction],
  );
  const { matchedValues, matchedValuesAncestors, topLevelNodesFiltered: topLevelNodes } = React.useMemo(
    () => filterTree(treeHierarchyInfo.topLevelNodes, searchText, itemFilterFn),
    [itemFilterFn, searchText, treeHierarchyInfo.topLevelNodes],
  );

  const { selectedValues, setSelectedValues, toggleSelection } = useSelectionState(treeHierarchyInfo, props);
  const { addToExpandedValues, expandedValues, setExpandedValues, toggleExpansion } = useExpansionState(props, matchedValues, matchedValuesAncestors);
  const { focusableValues, treeHeight } = React.useMemo(() => getFocusableValuesAndTreeHeight(topLevelNodes, expandedValues), [expandedValues, topLevelNodes]);
  const { activeValue, updateActiveValue } = usePseudoFocusState(focusableValues, selectedValues, treeId, hideSearchField);

  const handleClick = React.useCallback<React.MouseEventHandler<HTMLElement>>(e => {
    const item = (e.target as HTMLElement).closest('[role="treeitem"]');
    const itemValue = item?.getAttribute(ItemValueAttr);
    const isParent = item?.hasAttribute('aria-expanded');
    if (itemValue) {
      if (parentNodeActivationAction === 'toggleExpansion' && isParent) {
        toggleExpansion(itemValue);
      } else {
        toggleSelection(itemValue);
      }
      updateActiveValue(itemValue);
    }
    treeElemAttrs?.onClick?.(e);
  }, [parentNodeActivationAction, toggleExpansion, toggleSelection, treeElemAttrs, updateActiveValue]);

  return {
    treeAttributes: {
      'aria-activedescendant': activeValue ? treeItemId(treeId, activeValue) : undefined,
      'aria-multiselectable': multiselect,
      id: treeId,
      onClick: handleClick,
      style: { ...treeElemAttrs?.style, [`${TreeHeightCSSVarName}`]: treeHeight },
    },
    treeHierarchyInfo: {
      ...treeHierarchyInfo,
      topLevelNodes,
    },
    treeState: {
      activeValue,
      addToExpandedValues,
      expandedValues,
      focusableValues,
      matchedValues,
      selectedValues,
      setExpandedValues,
      setSelectedValues,
      toggleExpansion,
      toggleSelection,
      updateActiveValue,
    }
  };
};

const useDebouncedAnnouceCount = (searchFieldRef: React.RefObject<HTMLInputElement>, treeElemRef: React.RefObject<HTMLUListElement>) => {
  const announceTimeoutId = React.useRef(0);

  const announceCount = React.useCallback((timeout: number = 2000) => {
    window.clearTimeout(announceTimeoutId.current);

    announceTimeoutId.current = window.setTimeout(() => {
      const numItems = treeElemRef.current?.querySelectorAll('[role="treeitem"]').length || 0;
      let message = FilteringListMessages.optionCountAnnouncement({ optionCount: numItems, formattedOptionCount: Formatters.formatCount(numItems) })
      if (searchFieldRef.current?.value && numItems === 0) {
        message = FilteringListMessages.noMatches();
      }
      LiveAnnouncer.announce(message);
    }, timeout);
  }, [searchFieldRef, treeElemRef]);

  const clearAnnounceCount = React.useCallback(() => {
    window.clearTimeout(announceTimeoutId.current);
  }, []);

  return { announceCount, clearAnnounceCount };
};

const useExpansionState = (props: TreeProps, matchedValues: Set<string>, matchedValuesAncestors: Set<string>) => {
  const { expandedValues: expandedValuesControlled, initiallyExpandedValues, onExpansionChange } = props;

  const [expandedValuesUncontrolled, setExpandedValues] = React.useState(new Set<string>(initiallyExpandedValues));
  const expandedValues = expandedValuesControlled ?? expandedValuesUncontrolled;
  const isUncontrolled = !expandedValuesControlled;

  React.useEffect(() => {
    // make sure matched nodes from a search are visible by changing their ancestors to be expanded
    isUncontrolled && matchedValues.size && setExpandedValues(matchedValuesAncestors);
  }, [isUncontrolled, matchedValues, matchedValuesAncestors]);

  const toggleExpansion = React.useCallback((value?: string, expand?: boolean) => {
    if (!value) { return; }

    const alreadyExpanded = expandedValues.has(value);
    if (expand !== false && !alreadyExpanded) {
      const newExpandedValues = new Set(expandedValues);
      newExpandedValues.add(value);
      isUncontrolled && setExpandedValues(newExpandedValues);
      onExpansionChange?.(value);
    } else if (!expand && alreadyExpanded) {
      const newExpandedValues = new Set(expandedValues);
      newExpandedValues.delete(value);
      isUncontrolled && setExpandedValues(newExpandedValues);
      onExpansionChange?.(value);
    }
  }, [expandedValues, isUncontrolled, onExpansionChange]);

  const addToExpandedValues = React.useCallback((valuesToAdd: Array<string>) => {
    let added = false;
    if (isUncontrolled) {
      const newExpandedValues = new Set(expandedValues);
      valuesToAdd.forEach(value => {
        if (!expandedValues.has(value)) {
          newExpandedValues.add(value);
          added = true;
        }
      });
      added && setExpandedValues(newExpandedValues);
    }
  }, [expandedValues, isUncontrolled]);

  return { addToExpandedValues, expandedValues, setExpandedValues, toggleExpansion };
};

const getInitialSelection = (props: TreeProps) => {
  const { initiallySelectedValue, initiallySelectedValues, multiselect } = props;
  if (multiselect) {
    return initiallySelectedValues || [];
  } else {
    return initiallySelectedValue ? [initiallySelectedValue] : [];
  }
}

const useSelectedValueAsSet = (selectedValue: TreeProps['selectedValue']) => {
  return React.useMemo(() => {
    if (selectedValue === undefined) {
      return undefined;
    } else if (selectedValue === null) {
      return new Set<string>();
    } else {
      return new Set([selectedValue]);
    }
  }, [selectedValue]);
}

const useSelectionState = (treeHierarchyInfo: TreeHierarchyInfo, props: TreeProps) => {
  const { allSelectableLeafValues, allSelectableValues, nodeMapByValue } = treeHierarchyInfo;
  const { multiselect, onSelectionChange, selectedValues: selectedValuesControlled } = props;

  const [selectedValuesUncontrolled, setSelectedValues] = React.useState(new Set<string>(getInitialSelection(props)));
  const selectedValueAsSet = useSelectedValueAsSet(props.selectedValue);
  const selectedValues = (multiselect ? selectedValuesControlled : selectedValueAsSet) ?? selectedValuesUncontrolled;
  const isUncontrolled = !selectedValuesControlled && !selectedValueAsSet;

  const toggleSelection = React.useCallback((value?: string, select?: boolean) => {
    if (!value || !allSelectableValues.has(value)) { return; }
    const node = nodeMapByValue.get(value);
    if (!node) { return; }

    const newSelectedValues = multiselect ? new Set(selectedValues) : new Set<string>();
    const alreadySelected = selectedValues.has(value);
    const descendantLeafValues = node.children.length ? getDescendantLeafValues(nodeMapByValue, value) : [];
    const partiallySelected = multiselect && descendantLeafValues.filter(v => selectedValues.has(v)).length > 0;
    if (select !== false && !(alreadySelected || partiallySelected)) {
      if (isUncontrolled) {
        const selectableValues = multiselect ? allSelectableLeafValues : allSelectableValues;
        selectableValues.has(value) && newSelectedValues.add(value);
        multiselect && descendantLeafValues.forEach(v => {
          selectableValues.has(v) && newSelectedValues.add(v); // add values of all selectable descendants
        });
        setSelectedValues(newSelectedValues);
      }
      onSelectionChange?.(value);
    } else if (!select && (alreadySelected || partiallySelected)) {
      if (isUncontrolled) {
        newSelectedValues.delete(value);
        multiselect && descendantLeafValues.forEach(v => {
          newSelectedValues.delete(v); // remove values of all descendants (no need to check for selectable because it's safe to delete values not in the set)
        });
        setSelectedValues(newSelectedValues);
      }
      onSelectionChange?.(value);
    }
  }, [allSelectableLeafValues, allSelectableValues, isUncontrolled, multiselect, nodeMapByValue, onSelectionChange, selectedValues]);

  return { selectedValues, setSelectedValues, toggleSelection };
};

const usePseudoFocusState = (focusableValues: string[], selectedValues: Set<string>, treeId: string, hideSearchField?: boolean) => {
  const [activeValue, setActiveValue] = React.useState<string | undefined>(undefined);

  const updateActiveValue = React.useCallback((value?: string) => {
    if (value !== activeValue) {
      setActiveValue(value);
    }
  }, [activeValue]);

  React.useLayoutEffect(() => {
    activeValue && scrollIntoView(document.getElementById(treeItemId(treeId, activeValue)));
  }, [activeValue, treeId]);

  React.useEffect(() => {
    // make sure the active value is actually valid
    if (!activeValue || !focusableValues.includes(activeValue)) {
      if (hideSearchField && focusableValues.length) {
        updateActiveValue(focusableValues.find(v => selectedValues.has(v)) || focusableValues[0]);
      } else {
        updateActiveValue(undefined);
      }
    }
  }, [activeValue, focusableValues, hideSearchField, selectedValues, updateActiveValue]);

  return { activeValue, setActiveValue, updateActiveValue };
};

const useTreeChildElements = (
  treeId: string,
  treeHierarchyInfo: TreeHierarchyInfo,
  treeState: TreeState,
  multiselect?: boolean,
) => {
  const { allParentValuesLoadingWithin, allSelectableValues, nodeMapByValue, topLevelNodes } = treeHierarchyInfo;
  const { activeValue, expandedValues, selectedValues, toggleExpansion } = treeState;

  const renderTreeItems = React.useCallback((nodes: Array<TreeNode>, level: number) => {
    const setsize = nodes.filter(node => isFocusableItem(node.item)).length;
    let hiddenElems: Array<React.ReactElement> | undefined;
    let posinset = 0;

    return <>{nodes.map((node, index) => {
      if (!node.item.value || node.item.separator) {
        hiddenElems = undefined;
        return <div
          className={SeparatorStyleName}
          key={`separator%%${index}`}
          role='separator'
          style={{ [`${TreeNodeLevelCSSVarName}`]: level }}
        />;
      }

      if (node.item.group) {
        // need visually hidden elements to allow the section title to be read by assistive technology
        hiddenElems = hiddenElemsForSectionTitle(node.item.content || node.item.value);
        return <div
          children={node.item.content || node.item.value}
          className={SectionTitleStyleName}
          key={`section%%${index}`}
          style={{ [`${TreeNodeLevelCSSVarName}`]: level }}
        />;
      }

      let isSelected = selectedValues.has(node.item.value);
      let isSelectionIndeterminate = false;
      if (multiselect && node.children.length && !node.item.disabled) {
        const descendantLeafValues = getDescendantLeafValues(nodeMapByValue, node.item.value);
        const numDescendantsSelected = descendantLeafValues.filter(v => selectedValues.has(v)).length;
        isSelected = numDescendantsSelected === descendantLeafValues.length;
        if (numDescendantsSelected > 0 && numDescendantsSelected < descendantLeafValues.length) {
          isSelectionIndeterminate = true;
        }
      }

      posinset++; // one-based
      return <TreeItemElem
        aria-level={level + 1} // one-based
        aria-posinset={posinset}
        aria-setsize={setsize}
        hiddenElems={hiddenElems}
        id={treeItemId(treeId, node.item.value)}
        isActive={activeValue === node.item.value}
        isExpanded={expandedValues.has(node.item.value)}
        isSelected={isSelected}
        isSelectionIndeterminate={isSelectionIndeterminate}
        isUnselectable={!allSelectableValues.has(node.item.value)}
        item={node.item}
        key={node.item.value}
        loadingWithin={allParentValuesLoadingWithin.has(node.item.value)}
        nestedItems={node.children.length ? renderTreeItems(node.children, level + 1) : undefined}
        style={{ [`${TreeNodeLevelCSSVarName}`]: level }}
        toggleExpansion={toggleExpansion}
      />;
    })}</>;
  }, [activeValue, allParentValuesLoadingWithin, allSelectableValues, expandedValues, multiselect, nodeMapByValue, selectedValues, toggleExpansion, treeId]);

  const childElements = React.useMemo(() => renderTreeItems(topLevelNodes, 0), [renderTreeItems, topLevelNodes]);
  return childElements;
};

const hiddenElemsForSectionTitle = (content: React.ReactNode): Array<React.ReactElement> => {
  const stringParts = Messages.treeSectionPrefix({ ITEM_NAME: '%%ITEM_NAME%%', SECTION_NAME: '%%SECTION_NAME%%' }).split('%%ITEM_NAME%%');
  return stringParts.map((stringPart, index) => {
    if (stringPart.includes('%%SECTION_NAME%%')) {
      const stringPartsAroundSectionTitleContent = stringPart.split('%%SECTION_NAME%%');
      return (
        <span key={index} style={VisuallyHiddenStyle}>
          {stringPartsAroundSectionTitleContent[0]}
          {content}
          {stringPartsAroundSectionTitleContent[1]}
        </span>
      );
    } else {
      return stringPart.length ? <span key={index} style={VisuallyHiddenStyle}>{stringPart}</span> : <></>;
    }
  });
};

type TreeItemElemProps = Required<Pick<ElemAttrs, 'id' | 'aria-level' | 'aria-posinset' | 'aria-setsize' | 'style'>> & {
  hiddenElems?: Array<React.ReactElement>,
  isActive: boolean,
  isExpanded: boolean,
  isSelected: boolean,
  isSelectionIndeterminate: boolean,
  isUnselectable: boolean,
  item: TreeItem,
  loadingWithin: boolean,
  nestedItems?: React.ReactChild,
  toggleExpansion: (value?: string, expand?: boolean) => void,
};

const TreeItemElem: React.FC<TreeItemElemProps> = (props) => {
  // eslint-disable-next-line max-len
  const { hiddenElems, isActive, isExpanded, isSelected, isSelectionIndeterminate, isUnselectable, item, loadingWithin, nestedItems, toggleExpansion, ...htmlAttrs } = props;
  const groupDomId = React.useState(generateId())[0];
  const hasNestedItems = !!nestedItems;
  const contentLoading = isContentLoading(item);

  return <>
    <li
      {...htmlAttrs}
      {...{ [ItemValueAttr]: item.value }}
      aria-checked={isUnselectable ? undefined : isSelected}
      aria-disabled={(!hasNestedItems && (item.disabled || contentLoading)) ? true : undefined}
      aria-expanded={hasNestedItems ? isExpanded : undefined}
      // need `aria-owns` because the `role=group` element is a sibling for styling reasons but should be a descendant in the DOM,
      // see https://www.w3.org/WAI/ARIA/apg/patterns/treeview/ for details
      aria-owns={hasNestedItems && isExpanded ? groupDomId : undefined}
      aria-selected={isActive} // note that this attribute is used to indicate pseudo focus, while `aria-checked` is used to indicate user selection
      className={TypeStyle.classes(TreeItemStyleName, {
        [TreeNodeLoadingClassName]: contentLoading,
        [TreeNodeDisabledClassName]: item.disabled || contentLoading || loadingWithin,
      })}
      data-tb-test-id={item.testId}
      role='treeitem'
      title={item.tooltip}
    >
      <span className={ChevronContainerStyleName}>
        { // need a container to preserve space even when the chevron icon is not shown so that the content of sibling items can line up
          hasNestedItems &&
          <ArrowChevronRightIcon
            aria-hidden
            onClick={(e) => {
              toggleExpansion(item.value);
              e.stopPropagation();
            }}
            size={DefaultIconSize}
          />
        }
      </span>
      <span className={ContentContainerStyleName}>
        <span className={LabelContainerStyleName}>
          {hiddenElems?.[0]}
          {item.content || item.value}
          {hiddenElems?.[1]}
        </span>
        <span className={TypeStyle.classes(CheckContainerStyleName)}>
          {!isUnselectable && (isSelectionIndeterminate
            ? <OperationsSubtractMinusIcon aria-hidden size={CheckIconSize} />
            : isSelected && <NotificationCheckIcon aria-hidden size={CheckIconSize} />)
          }
        </span>
      </span>
    </li>
    {hasNestedItems && isExpanded &&
      <ul
        children={nestedItems}
        id={groupDomId}
        role='group'
      />
    }
  </>;
};
TreeItemElem.displayName = 'TreeItemElem';
